import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-normal transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        // Primary variants
        primary: "border-transparent bg-primary text-primary-foreground hover:bg-primary-dark active:bg-primary-light",
        "primary-light": "border-transparent bg-primary-light text-primary-foreground hover:bg-primary active:bg-primary-dark",
        "primary-dark": "border-transparent bg-primary-dark text-primary-foreground hover:bg-primary active:bg-primary-light",
        
        // Secondary variants
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        "secondary-light": "border-transparent bg-secondary-light text-secondary-foreground",
        "secondary-dark": "border-transparent bg-secondary-dark text-secondary-foreground",
        
        // Info variants
        info: "border-transparent bg-info text-info-foreground hover:bg-info-dark active:bg-info-light",
        "info-light": "border-transparent bg-info-light text-info-foreground hover:bg-info active:bg-info-dark",
        "info-dark": "border-transparent bg-info-dark text-info-foreground hover:bg-info active:bg-info-light",
        
        // Success variants
        success: "border-transparent bg-success text-success-foreground hover:bg-success-dark active:bg-success-light",
        "success-light": "border-transparent bg-success-light text-success-foreground hover:bg-success active:bg-success-dark",
        "success-dark": "border-transparent bg-success-dark text-success-foreground hover:bg-success active:bg-success-light",
        
        // Warning variants
        warning: "border-transparent bg-warning text-warning-foreground hover:bg-warning-dark active:bg-warning-light",
        "warning-light": "border-transparent bg-warning-light text-warning-foreground hover:bg-warning active:bg-warning-dark",
        "warning-dark": "border-transparent bg-warning-dark text-warning-foreground hover:bg-warning active:bg-warning-light",
        
        // Error variants
        error: "border-transparent bg-error text-error-foreground hover:bg-error-dark active:bg-error-light",
        "error-light": "border-transparent bg-error-light text-error-foreground hover:bg-error active:bg-error-dark",
        "error-dark": "border-transparent bg-error-dark text-error-foreground hover:bg-error active:bg-error-light",
        
        // Outline variants
        outline: "border-input bg-background hover:bg-action/10 active:bg-action/20",
        "outline-primary": "border-primary text-primary hover:bg-primary/10 active:bg-primary/20",
        "outline-secondary": "border-secondary text-secondary hover:bg-secondary/10 active:bg-secondary/20",
        "outline-info": "border-info text-info hover:bg-info/10 active:bg-info/20",
        "outline-success": "border-success text-success hover:bg-success/10 active:bg-success/20",
        "outline-warning": "border-warning text-warning hover:bg-warning/10 active:bg-warning/20",
        "outline-error": "border-error text-error hover:bg-error/10 active:bg-error/20",
      },
    },
    defaultVariants: {
      variant: "primary",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

export { Badge, badgeVariants }
